'use client';

/**
 * 用户详情页
 * 
 * @页面路径 /organization/members/:id
 * @权限要求 user:read:*
 * @文档参考 docs/modules/organization/05-ui-interaction-spec.md - 页面2: 用户详情页
 * 
 * 核心功能：
 * - 用户基本信息展示（头像、姓名、邮箱、电话等）
 * - 四个标签页：基本信息、部门归属、角色权限、操作日志
 * - 部门归属管理（添加、编辑、移除、设置主部门）
 * - 角色权限查看
 * - 懒加载机制（切换标签页时按需加载数据）
 */

import { useEffect, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { useTranslation } from '@/hooks/useTranslation';
import { usePermissions } from '@/hooks/usePermissions';
import { toast } from '@/lib/toast';
import { useConfirm } from '@/components/common/feedback/ConfirmProvider';
import { PageState } from '@/components/common/feedback/PageState';
import { handleApiError } from '@/lib/error-handler';
import apiClient from '@/lib/api-client';
import {
  ArrowLeft,
  Edit,
  MoreHorizontal,
  Mail,
  Phone,
  Briefcase,
  Building2,
  Calendar,
  Clock,
  User as UserIcon,
  Shield,
  Users,
  Copy,
  CheckCircle,
} from 'lucide-react';
import {
  getUserById,
  getUserDepartments,
  addUserDepartment,
  updateUserDepartment,
  removeUserDepartment,
  setUserPrimaryDepartment,
  getUserPermissions,
  getDepartments,
  getPositions,
  getUsers,
  type User,
  type UserStatus,
  type UserDepartment,
  type Department,
  type Position,
  type AddUserDepartmentDto,
} from '@/services/api/organization';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { colors, typography } from '@/styles/theme';

export default function UserDetailPage() {
  const { id } = useParams();
  const router = useRouter();
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { hasPermission, isAdmin } = usePermissions();

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [copiedField, setCopiedField] = useState<string | null>(null);
  
  // 部门归属相关状态
  const [departments, setDepartments] = useState<UserDepartment[]>([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [showAddDeptDialog, setShowAddDeptDialog] = useState(false);
  const [editingDept, setEditingDept] = useState<UserDepartment | null>(null);
  
  // 添加部门归属表单状态
  const [allDepartments, setAllDepartments] = useState<Department[]>([]);
  const [allPositions, setAllPositions] = useState<Position[]>([]);
  const [deptManagers, setDeptManagers] = useState<User[]>([]);
  const [addDeptForm, setAddDeptForm] = useState<AddUserDepartmentDto>({
    departmentId: '',
    positionId: '',
    managerId: '',
    isPrimary: false,
    title: '',
  });
  const [submittingDept, setSubmittingDept] = useState(false);
  
  // 权限相关状态
  const [userPermissions, setUserPermissions] = useState<{
    permissions: Array<string | { code: string }>;
    roles: Array<{ id: string; name: string; code: string }>;
  } | null>(null);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  
  // 审计日志相关状态
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  // v2.4 SSO 信息块：最近一次 SSO 登录时间，从 AuditLog SSO_LOGIN_SUCCESS 反查
  const [lastSsoLogin, setLastSsoLogin] = useState<string | null>(null);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const canUpdate = isAdmin || hasPermission('user:update');
  const canManageRoles = isAdmin || hasPermission('role:assign');

  useEffect(() => {
    loadUserDetail();
  }, [id]);

  useEffect(() => {
    if (activeTab === 'departments' && user && departments.length === 0) {
      loadDepartments();
    }
    if (activeTab === 'roles' && user && !userPermissions) {
      loadPermissions();
    }
    if (activeTab === 'logs' && user && auditLogs.length === 0) {
      loadAuditLogs();
    }
  }, [activeTab, user]);

  const loadUserDetail = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const userData = await getUserById(id as string);
      setUser(userData);
    } catch (err: any) {
      setError(err.message || t.organization.userDetail.loadFailed);
      handleApiError(err, t.locale, { prefix: t.organization.userDetail.loadFailed });
    } finally {
      setLoading(false);
    }
  };

  const loadDepartments = async () => {
    if (!id) return;
    try {
      setLoadingDepartments(true);
      const depts = await getUserDepartments(id as string);
      setDepartments(depts);
    } catch (err: any) {
      handleApiError(err, t.locale, { prefix: t.organization.userDetail.departments.loadFailed });
    } finally {
      setLoadingDepartments(false);
    }
  };

  const loadPermissions = async () => {
    if (!id) return;
    try {
      setLoadingPermissions(true);
      const permData = await getUserPermissions(id as string);
      setUserPermissions({
        permissions: permData.permissions || [],
        roles: permData.roles || [],
      });
    } catch (err: any) {
      handleApiError(err, t.locale, { prefix: t.organization.userDetail.rolesPermissions.loadFailed });
    } finally {
      setLoadingPermissions(false);
    }
  };

  // v2.4 SSO 信息块：拉最近 SSO 登录时间
  // 路径：GET /api/v1/audit/logs?userId=<id>&action=SSO_LOGIN_SUCCESS&limit=1
  // 失败静默 fallback 到 neverLoggedIn（仅展示用，不阻塞页面）
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    apiClient
      .get('/audit/logs', {
        params: { userId: user.id, action: 'SSO_LOGIN_SUCCESS', limit: 1 },
      })
      .then((res: any) => {
        if (cancelled) return;
        const items =
          (Array.isArray(res?.data) && res.data) ||
          res?.data?.items ||
          res?.data?.data ||
          res?.items ||
          [];
        // backend prisma AuditLog 字段名为 `when`（spec），其它 fallback 容忍 wrapper 变化
        const ts =
          items[0]?.when ||
          items[0]?.timestamp ||
          items[0]?.createdAt ||
          items[0]?.created_at ||
          null;
        setLastSsoLogin(ts);
      })
      .catch(() => {
        if (!cancelled) setLastSsoLogin(null);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const loadAuditLogs = async () => {
    if (!id) return;
    try {
      setLoadingLogs(true);
      // TODO: 实现审计日志 API 调用
      // const logs = await getUserAuditLogs(id as string);
      // setAuditLogs(logs);
      setAuditLogs([]); // 临时空数组
    } catch (err: any) {
      handleApiError(err, t.locale, { prefix: t.organization.userDetail.logs.loadFailed });
    } finally {
      setLoadingLogs(false);
    }
  };

  const loadDeptOptions = async () => {
    try {
      const [depts, positions] = await Promise.all([
        getDepartments(),
        getPositions(),
      ]);
      const deptList = Array.isArray(depts) ? depts : ((depts as any).items || []);
      setAllDepartments(deptList);
      setAllPositions(positions || []);
    } catch (err: any) {
      handleApiError(err, t.locale, { prefix: t.common.loadFailed });
    }
  };

  const loadDeptManagers = async (departmentId: string) => {
    try {
      const users = await getUsers({ departmentId, status: 'ACTIVE' });
      setDeptManagers(users.items || []);
    } catch (err: any) {
      console.error('Failed to load managers:', err);
    }
  };

  const handleOpenAddDeptDialog = async () => {
    await loadDeptOptions();
    setAddDeptForm({
      departmentId: '',
      positionId: '',
      managerId: '',
      isPrimary: false,
      title: '',
    });
    setShowAddDeptDialog(true);
  };

  const handleAddDepartment = async () => {
    if (!id || !addDeptForm.departmentId) {
      toast.error(t.organization.userDetail.departments.selectDepartment);
      return;
    }

    try {
      setSubmittingDept(true);
      await addUserDepartment(id as string, {
        departmentId: addDeptForm.departmentId,
        positionId: addDeptForm.positionId || undefined,
        managerId: addDeptForm.managerId || undefined,
        title: addDeptForm.title || undefined,
        isPrimary: addDeptForm.isPrimary,
      });
      toast.success(t.organization.userDetail.departments.addSuccess);
      setShowAddDeptDialog(false);
      await loadDepartments();
      await loadUserDetail(); // 刷新用户信息
    } catch (err: any) {
      handleApiError(err, t.locale, { prefix: t.organization.userDetail.departments.addFailed });
    } finally {
      setSubmittingDept(false);
    }
  };

  const handleEditDepartment = async (membership: UserDepartment | { id: string; departmentId: string; positionId?: string | null; managerId?: string | null; isPrimary: boolean; title?: string | null; department?: { id: string; name: string; code?: string }; position?: { id: string; name: string; level?: number }; manager?: { id: string; displayName: string; email?: string } }) => {
    // 转换为完整的 UserDepartment 类型（如果缺少字段）
    const fullMembership: UserDepartment = {
      id: membership.id,
      userId: id as string,
      departmentId: membership.departmentId,
      positionId: membership.positionId || null,
      managerId: membership.managerId || null,
      isPrimary: membership.isPrimary,
      title: membership.title || null,
      // ⭐ 修复：使用实际时间，避免用 new Date() 作为兜底
      createdAt: (membership as any).createdAt || '',
      updatedAt: (membership as any).updatedAt || '',
      department: membership.department ? {
        id: membership.department.id,
        name: membership.department.name,
        code: membership.department.code || '',
      } : undefined,
      position: membership.position,
      manager: membership.manager,
    };
    
    setEditingDept(fullMembership);
    await loadDeptOptions();
    if (fullMembership.departmentId) {
      await loadDeptManagers(fullMembership.departmentId);
    }
    setAddDeptForm({
      departmentId: fullMembership.departmentId,
      positionId: fullMembership.positionId || '',
      managerId: fullMembership.managerId || '',
      isPrimary: fullMembership.isPrimary,
      title: fullMembership.title || '',
    });
    setShowAddDeptDialog(true);
  };

  const handleUpdateDepartment = async () => {
    if (!id || !editingDept || !addDeptForm.departmentId) {
      toast.error(t.organization.userDetail.departments.selectDepartment);
      return;
    }

    try {
      setSubmittingDept(true);
      // 更新基本信息
      await updateUserDepartment(id as string, editingDept.departmentId, {
        positionId: addDeptForm.positionId || undefined,
        managerId: addDeptForm.managerId || undefined,
        title: addDeptForm.title || undefined,
      });
      
      // 如果设置了主部门,单独调用 API
      if (addDeptForm.isPrimary && !editingDept.isPrimary) {
        await setUserPrimaryDepartment(id as string, editingDept.departmentId);
      }
      
      toast.success(t.organization.userDetail.departments.updateSuccess);
      setShowAddDeptDialog(false);
      setEditingDept(null);
      await loadDepartments();
      await loadUserDetail();
    } catch (err: any) {
      handleApiError(err, t.locale, { prefix: t.organization.userDetail.departments.updateFailed });
    } finally {
      setSubmittingDept(false);
    }
  };

  const handleRemoveDepartment = async (membership: UserDepartment | { id: string; departmentId: string; department?: { id: string; name: string; code?: string } }) => {
    const deptName = membership.department?.name || t.organization.userDetail.departments.thisDepartment;
    const confirmed = await confirm({
      title: t.organization.userDetail.departments.confirmRemove.replace('{name}', deptName),
      variant: 'danger',
    });
    if (!confirmed) return;

    if (!id) return;

    try {
      await removeUserDepartment(id as string, membership.departmentId);
      toast.success(t.organization.userDetail.departments.removeSuccess);
      await loadDepartments();
      await loadUserDetail();
    } catch (err: any) {
      handleApiError(err, t.locale, { prefix: t.organization.userDetail.departments.removeFailed });
    }
  };

  const handleSetPrimaryDepartment = async (membership: UserDepartment | { id: string; departmentId: string }) => {
    if (!id) return;

    try {
      await setUserPrimaryDepartment(id as string, membership.departmentId);
      toast.success(t.organization.userDetail.departments.setPrimarySuccess);
      await loadDepartments();
      await loadUserDetail();
    } catch (err: any) {
      handleApiError(err, t.locale, { prefix: t.organization.userDetail.departments.setPrimaryFailed });
    }
  };

  const handleBack = () => {
    router.push('/organization/members');
  };

  const handleEdit = () => {
    router.push(`/organization/members/${id}/edit`);
  };

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success(t.common.copySuccess);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error(t.common.copyFailed);
    }
  };

  const getStatusBadge = (status: UserStatus) => {
    const statusMap = {
      ACTIVE: {
        bg: 'bg-[#e6f7ed]',
        text: 'text-[#00b42a]',
        icon: '🟢',
        label: t.organization.statusActive,
      },
      INACTIVE: {
        bg: 'bg-[#f7f8fa]',
        text: 'text-[#8f959e]',
        icon: '🟡',
        label: t.organization.statusInactive,
      },
      SUSPENDED: {
        bg: 'bg-[#fff7e6]',
        text: 'text-[#ff7d00]',
        icon: '🟠',
        label: t.organization.statusSuspended,
      },
      TERMINATED: {
        bg: 'bg-[#ffece8]',
        text: 'text-[#f53f3f]',
        icon: '🔴',
        label: t.organization.statusTerminated,
      },
    };

    const config = statusMap[status];
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded ${config.bg} ${config.text}`}
        data-testid={`status-badge-${status}`}
      >
        <span>{config.icon}</span>
        <span>{config.label}</span>
      </span>
    );
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const formatDateTime = (dateString?: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="h-full flex flex-col" style={{ backgroundColor: colors.bgSecondary }}>
        <div className="bg-white border-b" style={{ borderColor: colors.border }}>
          <div className="px-6 h-12 flex items-center">
            <Skeleton className="h-6 w-48" />
          </div>
        </div>
        <div className="flex-1 overflow-auto p-6">
          <Skeleton className="h-48 w-full mb-6" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="h-full flex items-center justify-center" style={{ backgroundColor: colors.bgSecondary }}>
        <div className="text-center p-6 bg-white rounded-lg shadow-sm" style={{ border: `1px solid ${colors.border}` }}>
          <UserIcon className="w-12 h-12 mx-auto mb-4" style={{ color: colors.error }} />
          <h3 className={typography.h3}>{error || t.organization.userDetail.notFound}</h3>
          <p className={typography.bodySecondary + ' mt-2'}>
            {t.organization.userDetail.notFound}
          </p>
          <Button onClick={handleBack} className="mt-4">
            {t.organization.userDetail.backToList}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: colors.bgSecondary }}>
      {/* Header */}
      <div className="bg-white border-b" style={{ borderColor: colors.border }}>
        <div className="px-6 h-12 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              data-action="back"
              className="flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              {t.common.back}
            </Button>
            <h1 className={typography.h4} data-testid="user-title">
              {user.displayName} ({user.username})
            </h1>
            {getStatusBadge(user.status)}
          </div>
          <div className="flex items-center gap-2">
            {canUpdate && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleEdit}
                data-action="edit"
                className="flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                {t.common.edit}
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-action="more">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canManageRoles && (
                  <DropdownMenuItem data-action="assign-roles">
                    <Shield className="mr-2 h-4 w-4" />
                    {t.organization.assignRole}
                  </DropdownMenuItem>
                )}
                {canUpdate && user.status === 'ACTIVE' && (
                  <DropdownMenuItem data-action="deactivate">
                    {t.organization.disable}
                  </DropdownMenuItem>
                )}
                {canUpdate && user.status !== 'ACTIVE' && (
                  <DropdownMenuItem data-action="activate">
                    {t.organization.activate}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {/* User Header Card */}
          <div className="bg-white rounded-lg p-6 mb-6 shadow-sm" style={{ border: `1px solid ${colors.border}` }}>
            <div className="flex items-start gap-6">
              <Avatar className="w-20 h-20" data-testid="user-avatar">
                <AvatarImage src={user.avatar || undefined} />
                <AvatarFallback style={{ backgroundColor: colors.primary, color: colors.bgPrimary }}>
                  {user.displayName.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h2 className={typography.h2 + ' mb-2'}>{user.displayName}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2" style={{ color: colors.textSecondary }}>
                    <UserIcon className="w-4 h-4" style={{ color: colors.textTertiary }} />
                    <span>{user.username}</span>
                  </div>
                  {user.email && (
                    <div className="flex items-center gap-2" style={{ color: colors.textSecondary }}>
                      <Mail className="w-4 h-4" style={{ color: colors.textTertiary }} />
                      <span>{user.email}</span>
                      <button
                        onClick={() => handleCopy(user.email, 'email')}
                        className="ml-1 hover:opacity-70"
                      >
                        {copiedField === 'email' ? (
                          <CheckCircle className="w-3 h-3" style={{ color: colors.success }} />
                        ) : (
                          <Copy className="w-3 h-3" style={{ color: colors.textTertiary }} />
                        )}
                      </button>
                    </div>
                  )}
                  {user.phone && (
                    <div className="flex items-center gap-2" style={{ color: colors.textSecondary }}>
                      <Phone className="w-4 h-4" style={{ color: colors.textTertiary }} />
                      <span>{user.phone}</span>
                    </div>
                  )}
                  {user.department && (
                    <div className="flex items-center gap-2" style={{ color: colors.textSecondary }}>
                      <Building2 className="w-4 h-4" style={{ color: colors.textTertiary }} />
                      <span>{user.department.name}</span>
                    </div>
                  )}
                  {user.position && (
                    <div className="flex items-center gap-2" style={{ color: colors.textSecondary }}>
                      <Briefcase className="w-4 h-4" style={{ color: colors.textTertiary }} />
                      <span>{user.position.name}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="tabs">
            <TabsList className="bg-white rounded-lg p-1 mb-6" style={{ border: `1px solid ${colors.border}` }}>
              <TabsTrigger value="basic" data-tab="basic">
                {t.organization.userDetail.tabs.basicInfo}
              </TabsTrigger>
              <TabsTrigger value="departments" data-tab="departments">
                {t.organization.userDetail.tabs.departments}
              </TabsTrigger>
              <TabsTrigger value="roles" data-tab="roles">
                {t.organization.userDetail.tabs.rolesPermissions}
              </TabsTrigger>
              <TabsTrigger value="logs" data-tab="logs">
                {t.organization.userDetail.tabs.logs}
              </TabsTrigger>
            </TabsList>

            {/* Basic Info Tab */}
            <TabsContent value="basic" data-tab-panel="basic">
              <div className="bg-white rounded-lg p-6 shadow-sm" style={{ border: `1px solid ${colors.border}` }} data-testid="user-basic-info">
                <h3 className={typography.h3 + ' mb-4'}>{t.organization.userDetail.basicInfo.title}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={typography.bodySecondary}>{t.organization.username}</label>
                    <p className={typography.body + ' mt-1'} data-field="username">{user.username}</p>
                  </div>
                  <div>
                    <label className={typography.bodySecondary}>{t.organization.realName}</label>
                    <p className={typography.body + ' mt-1'} data-field="displayName">{user.displayName}</p>
                  </div>
                  <div>
                    <label className={typography.bodySecondary}>{t.organization.email}</label>
                    <p className={typography.body + ' mt-1'} data-field="email">{user.email || '-'}</p>
                  </div>
                  <div>
                    <label className={typography.bodySecondary}>{t.organization.phone}</label>
                    <p className={typography.body + ' mt-1'} data-field="phone">{user.phone || '-'}</p>
                  </div>
                  <div>
                    <label className={typography.bodySecondary}>{t.organization.userDetail.basicInfo.employeeId}</label>
                    <p className={typography.body + ' mt-1'} data-field="employeeId">{user.employeeId || '-'}</p>
                  </div>
                  <div>
                    <label className={typography.bodySecondary}>{t.organization.status}</label>
                    <div className="mt-1" data-field="status">{getStatusBadge(user.status)}</div>
                  </div>
                  <div>
                    <label className={typography.bodySecondary}>{t.organization.source}</label>
                    <p className={typography.body + ' mt-1'} data-field="source">
                      {user.source === 'LOCAL' ? t.organization.manualCreated : user.source === 'ENTRA' ? 'Entra ID' : user.source}
                    </p>
                  </div>
                  <div>
                    <label className={typography.bodySecondary}>{t.organization.members.region}</label>
                    <p className={typography.body + ' mt-1'} data-field="defaultRegion">{user.region || '-'}</p>
                  </div>
                  <div>
                    <label className={typography.bodySecondary}>{t.organization.userDetail.basicInfo.workCity}</label>
                    <p className={typography.body + ' mt-1'} data-field="workCity">{user.workCity || '-'}</p>
                  </div>
                  <div>
                    <label className={typography.bodySecondary}>{t.organization.joinDate}</label>
                    <p className={typography.body + ' mt-1'} data-field="joinedAt">{formatDate(user.hiredAt)}</p>
                  </div>
                  <div>
                    <label className={typography.bodySecondary}>{t.organization.userDetail.basicInfo.createdAt}</label>
                    <p className={typography.body + ' mt-1'} data-field="createdAt">{formatDateTime(user.createdAt)}</p>
                  </div>
                  {user.ldapSyncedAt && (
                    <div className="col-span-full">
                      <label className={typography.bodySecondary}>{t.organization.userDetail.basicInfo.lastSyncedAt}</label>
                      <p className={typography.body + ' mt-1'} data-field="lastSyncedAt">{formatDateTime(user.ldapSyncedAt)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* v2.4 SSO 绑定信息（只读分块） */}
              {/* 可见性：itadmin / Administrator 角色或拥有 user:read:all 权限。 */}
              {/* 事实源：docs/modules/organization/05-ui-interaction-spec.md「SSO 绑定信息」 */}
              {(isAdmin || hasPermission('user:read:all')) && (
                <div
                  className="bg-white rounded-lg p-6 shadow-sm mt-4"
                  style={{ border: `1px solid ${colors.border}` }}
                  data-testid="user-sso-section"
                >
                  <h3 className={typography.h3 + ' mb-4'}>{t.users.detail.sso.sectionTitle}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* 外部身份 ID */}
                    <div>
                      <label className={typography.bodySecondary}>{t.users.detail.sso.externalId}</label>
                      <div className="mt-1 flex items-center gap-2 group">
                        <p
                          className="font-mono text-sm"
                          style={{ color: colors.textPrimary }}
                          data-field="externalId"
                        >
                          {user.externalId || '—'}
                        </p>
                        {user.externalId && (
                          <button
                            type="button"
                            data-action="copy-external-id"
                            onClick={() => handleCopy(user.externalId!, 'externalId')}
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label={t.users.detail.sso.copyId}
                            title={t.users.detail.sso.copyId}
                          >
                            {copiedField === 'externalId' ? (
                              <CheckCircle className="w-3 h-3" style={{ color: colors.success }} />
                            ) : (
                              <Copy className="w-3 h-3" style={{ color: colors.textTertiary }} />
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* 外部身份来源 */}
                    <div>
                      <label className={typography.bodySecondary}>{t.users.detail.sso.externalSource}</label>
                      <div className="mt-1" data-field="externalSource">
                        {(() => {
                          const src = (user.externalSource || '').toLowerCase();
                          if (src === 'entra') {
                            return (
                              <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-blue-200">
                                {t.users.detail.sso.source.entra}
                              </Badge>
                            );
                          }
                          if (src === 'ldap') {
                            // ldap + externalId 非空 → "LDAP / 待 SSO 升级"（spec 三态规则）
                            const label = user.externalId
                              ? t.users.detail.sso.source.ldapPending
                              : t.users.detail.sso.source.ldap;
                            return (
                              <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100 border-gray-200">
                                {label}
                              </Badge>
                            );
                          }
                          // NULL / 未知 → 未绑定
                          return (
                            <span
                              className={typography.body}
                              style={{ color: colors.textTertiary }}
                            >
                              {t.users.detail.sso.source.none}
                            </span>
                          );
                        })()}
                      </div>
                    </div>

                    {/* 最近 SSO 登录时间：v2.4 从 AuditLog SSO_LOGIN_SUCCESS 反查（spec 06-data-model.md） */}
                    <div className="col-span-full">
                      <label className={typography.bodySecondary}>{t.users.detail.sso.lastLoginAt}</label>
                      <p
                        className={typography.body + ' mt-1'}
                        style={{ color: lastSsoLogin ? colors.textPrimary : colors.textTertiary }}
                        data-field="lastSsoLoginAt"
                      >
                        {lastSsoLogin ? formatDateTime(lastSsoLogin) : t.users.detail.sso.neverLoggedIn}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Departments Tab */}
            <TabsContent value="departments" data-tab-panel="departments" data-testid="tab-content">
              <div className="bg-white rounded-lg p-6 shadow-sm" style={{ border: `1px solid ${colors.border}` }} data-testid="user-departments">
                <div className="flex items-center justify-between mb-4">
                  <h3 className={typography.h3}>{t.organization.userDetail.departments.title}</h3>
                  {canUpdate && (
                    <Button size="sm" data-action="add-department" onClick={handleOpenAddDeptDialog}>
                      {t.organization.userDetail.departments.addDepartment}
                    </Button>
                  )}
                </div>
                {loadingDepartments ? (
                  <div className="space-y-4">
                    {[1, 2].map((i) => (
                      <Skeleton key={i} className="h-20 w-full" />
                    ))}
                  </div>
                ) : (departments.length > 0 || (user.departmentMemberships && user.departmentMemberships.length > 0)) ? (
                  <div className="space-y-4" data-testid="department-list">
                    {(departments.length > 0 ? departments : user.departmentMemberships || []).map((membership, idx) => (
                      <div
                        key={membership.id}
                        className="flex items-start justify-between p-4 rounded-lg"
                        style={{ backgroundColor: colors.bgSecondary }}
                        data-testid={`department-item-${idx}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className={typography.h4} data-field="department-name">{membership.department?.name}</h4>
                            {membership.isPrimary && (
                              <Badge variant="default" style={{ backgroundColor: colors.primary }} data-testid="primary-badge">
                                {t.organization.userDetail.departments.primaryBadge}
                              </Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            {membership.position && (
                              <div>
                                <span className={typography.bodySecondary}>{t.organization.position}: </span>
                                <span className={typography.body} data-field="position-name">{membership.position.name}</span>
                              </div>
                            )}
                            {membership.title && (
                              <div>
                                <span className={typography.bodySecondary}>{t.organization.userDetail.departments.jobTitle}: </span>
                                <span className={typography.body} data-field="job-title">{membership.title}</span>
                              </div>
                            )}
                            {membership.manager && (
                              <div>
                                <span className={typography.bodySecondary}>{t.organization.manager}: </span>
                                <span className={typography.body} data-field="manager-name">{membership.manager.displayName}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        {canUpdate && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-action="department-more">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEditDepartment(membership)} data-action="edit-department">
                                <Edit className="mr-2 h-4 w-4" />
                                {t.common.edit}
                              </DropdownMenuItem>
                              {!membership.isPrimary && (
                                <DropdownMenuItem onClick={() => handleSetPrimaryDepartment(membership)} data-action="set-primary">
                                  {t.organization.userDetail.departments.setPrimary}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem 
                                onClick={() => handleRemoveDepartment(membership)}
                                className="text-red-600"
                                data-action="remove-department"
                              >
                                <Users className="mr-2 h-4 w-4" />
                                {t.common.remove}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-12" data-testid="empty-departments">
                    <PageState
                      variant="empty"
                      title={t.organization.userDetail.departments.noDepartments}
                      layout="center"
                      className="border-0 bg-transparent"
                    />
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Roles & Permissions Tab */}
            <TabsContent value="roles" data-tab-panel="roles">
              <div className="bg-white rounded-lg p-6 shadow-sm" style={{ border: `1px solid ${colors.border}` }} data-testid="user-roles">
                <h3 className={typography.h3 + ' mb-4'}>{t.organization.userDetail.rolesPermissions.rolesSection}</h3>
                {loadingPermissions ? (
                  <div className="space-y-2">
                    {[1, 2].map((i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : (userPermissions?.roles && userPermissions.roles.length > 0) || (user.roles && user.roles.length > 0) ? (
                  <div className="space-y-2 mb-6" data-testid="role-list">
                    {(userPermissions?.roles || user.roles || []).map((role) => (
                      <div
                        key={role.id}
                        className="flex items-center gap-3 p-3 rounded-lg"
                        style={{ backgroundColor: colors.bgSecondary }}
                        data-testid="role-item"
                      >
                        <Shield className="w-5 h-5" style={{ color: colors.primary }} />
                        <div>
                          <h4 className={typography.h4} data-field="role-name">{role.name}</h4>
                          <p className={typography.caption} data-field="role-code">{role.code}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-12" data-testid="empty-roles">
                    <PageState
                      variant="empty"
                      title={t.organization.userDetail.rolesPermissions.noRoles}
                      layout="center"
                      className="border-0 bg-transparent"
                    />
                  </div>
                )}
                
                {/* Permissions List */}
                {userPermissions && userPermissions.permissions.length > 0 && (
                  <div className="mt-6" data-testid="permissions-section">
                    <h3 className={typography.h3 + ' mb-4'}>{t.organization.userDetail.rolesPermissions.permissionsSection}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="permission-list">
                      {userPermissions.permissions.map((permission, index) => (
                        <div
                          key={index}
                          className="p-2 rounded text-sm"
                          style={{ backgroundColor: colors.bgSecondary }}
                          data-testid="permission-item"
                        >
                          <code className="text-xs" style={{ color: colors.textSecondary }}>
                            {typeof permission === 'string' ? permission : permission.code}
                          </code>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Audit Logs Tab */}
            <TabsContent value="logs" data-tab-panel="logs">
              <div className="bg-white rounded-lg p-6 shadow-sm" style={{ border: `1px solid ${colors.border}` }} data-testid="user-logs">
                <h3 className={typography.h3 + ' mb-4'}>{t.organization.userDetail.tabs.logs}</h3>
                {loadingLogs ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : auditLogs.length > 0 ? (
                  <div className="space-y-2" data-testid="log-list">
                    {auditLogs.map((log, index) => (
                      <div
                        key={index}
                        className="p-4 rounded-lg border"
                        style={{ borderColor: colors.border }}
                        data-testid="log-item"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={typography.body} data-field="log-action">{log.action || t.organization.userDetail.logs.unknownAction}</p>
                            <p className={typography.caption} style={{ color: colors.textSecondary }} data-field="log-time">
                              {log.createdAt ? new Date(log.createdAt).toLocaleString() : '-'}
                            </p>
                          </div>
                          <Badge variant="secondary" data-field="log-type">{log.type || 'INFO'}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-12" data-testid="empty-logs">
                    <PageState
                      variant="empty"
                      title={t.organization.userDetail.logs.noLogs}
                      layout="center"
                      className="border-0 bg-transparent"
                    />
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Add/Edit Department Dialog */}
      <Dialog open={showAddDeptDialog} onOpenChange={setShowAddDeptDialog} data-testid="add-department-dialog">
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingDept ? t.organization.userDetail.departments.editDialog.title : t.organization.userDetail.departments.addDialog.title}
            </DialogTitle>
            <DialogDescription>
              {editingDept ? t.organization.userDetail.departments.editDialog.description : t.organization.userDetail.departments.addDialog.description}
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (editingDept) {
                handleUpdateDepartment();
              } else {
                handleAddDepartment();
              }
            }}
            data-form="user-department"
            className="space-y-4"
          >
            <div>
              <Label htmlFor="departmentId">
                {t.organization.department} <span style={{ color: colors.error }}>*</span>
              </Label>
              <Select
                value={addDeptForm.departmentId}
                onValueChange={(value) => {
                  setAddDeptForm({ ...addDeptForm, departmentId: value, managerId: '' });
                  loadDeptManagers(value);
                }}
                disabled={!!editingDept}
              >
                <SelectTrigger className="mt-1" id="departmentId" name="departmentId">
                  <SelectValue placeholder={t.common.selectDepartment} />
                </SelectTrigger>
                <SelectContent>
                  {allDepartments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="positionId">{t.organization.position}</Label>
              <Select
                value={addDeptForm.positionId || 'none'}
                onValueChange={(value) => setAddDeptForm({ ...addDeptForm, positionId: value === 'none' ? '' : value })}
              >
                <SelectTrigger className="mt-1" id="positionId" name="positionId">
                  <SelectValue placeholder={t.common.selectPosition} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t.common.none}</SelectItem>
                  {allPositions.map((pos) => (
                    <SelectItem key={pos.id} value={pos.id}>
                      {pos.name} {pos.level && `(L${pos.level})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="managerId">{t.organization.userDetail.departments.directManager}</Label>
              <Select
                value={addDeptForm.managerId || 'none'}
                onValueChange={(value) => setAddDeptForm({ ...addDeptForm, managerId: value === 'none' ? '' : value })}
                disabled={!addDeptForm.departmentId}
              >
                <SelectTrigger className="mt-1" id="managerId" name="managerId">
                  <SelectValue placeholder={addDeptForm.departmentId ? t.common.selectManager : t.common.selectDepartmentFirst} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t.common.none}</SelectItem>
                  {deptManagers
                    .filter((m) => m.id !== id)
                    .map((manager) => (
                      <SelectItem key={manager.id} value={manager.id}>
                        {manager.displayName} ({manager.username})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="title">{t.organization.userDetail.departments.jobTitle}</Label>
              <Input
                id="title"
                name="title"
                value={addDeptForm.title || ''}
                onChange={(e) => setAddDeptForm({ ...addDeptForm, title: e.target.value })}
                placeholder={t.organization.userDetail.departments.jobTitlePlaceholder}
                className="mt-1"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="isPrimary"
                name="isPrimary"
                checked={addDeptForm.isPrimary}
                onCheckedChange={(checked) => setAddDeptForm({ ...addDeptForm, isPrimary: !!checked })}
              />
              <Label htmlFor="isPrimary" className="cursor-pointer">
                {t.organization.userDetail.departments.setAsPrimary}
              </Label>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddDeptDialog(false);
                  setEditingDept(null);
                }}
                data-action="cancel"
              >
                {t.common.cancel}
              </Button>
              <Button type="submit" disabled={submittingDept || !addDeptForm.departmentId} data-action="submit">
                {submittingDept ? t.common.submitting : editingDept ? t.common.update : t.common.add}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
